/**
 * Logging service.
 *
 * A single, reusable logger used across the whole application — startup,
 * errors, warnings and information. It:
 *
 *   - keeps a bounded in-memory ring buffer of recent entries (for an in-app
 *     log viewer later),
 *   - mirrors entries to the browser console with consistent formatting,
 *   - forwards to Tauri's native log plugin when running inside the shell,
 *   - lets callers create scoped child loggers so every message is tagged with
 *     its origin (e.g. `logger.scope("router")`).
 *
 * Import the shared instance:  `import { logger } from "@/services/logger";`
 */
import type { LogEntry, LogLevel } from "@/types";
import { createId, isTauri } from "@/utils";

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const MAX_BUFFER = 500;

type Listener = (entry: LogEntry) => void;

/** Lazily-loaded Tauri log functions, resolved once at first use. */
interface TauriLog {
  debug: (m: string) => Promise<void>;
  info: (m: string) => Promise<void>;
  warn: (m: string) => Promise<void>;
  error: (m: string) => Promise<void>;
}

class Logger {
  private buffer: LogEntry[] = [];
  private listeners = new Set<Listener>();
  private minLevel: LogLevel = import.meta.env.DEV ? "debug" : "info";
  private tauriLog: TauriLog | null = null;
  private tauriLoadAttempted = false;

  /** Raise or lower the threshold below which entries are dropped. */
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  debug(scope: string, message: string, data?: unknown): void {
    this.emit("debug", scope, message, data);
  }
  info(scope: string, message: string, data?: unknown): void {
    this.emit("info", scope, message, data);
  }
  warn(scope: string, message: string, data?: unknown): void {
    this.emit("warn", scope, message, data);
  }
  error(scope: string, message: string, data?: unknown): void {
    this.emit("error", scope, message, data);
  }

  /** A bound logger that stamps every message with a fixed scope. */
  scope(scope: string) {
    return {
      debug: (m: string, d?: unknown) => this.debug(scope, m, d),
      info: (m: string, d?: unknown) => this.info(scope, m, d),
      warn: (m: string, d?: unknown) => this.warn(scope, m, d),
      error: (m: string, d?: unknown) => this.error(scope, m, d),
    };
  }

  /** Snapshot of the current buffer (most recent last). */
  getEntries(): readonly LogEntry[] {
    return this.buffer;
  }

  clear(): void {
    this.buffer = [];
  }

  /** Subscribe to new entries. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(
    level: LogLevel,
    scope: string,
    message: string,
    data?: unknown,
  ): void {
    if (LEVEL_ORDER[level] < LEVEL_ORDER[this.minLevel]) return;

    const entry: LogEntry = {
      id: createId("log"),
      level,
      scope,
      message,
      timestamp: Date.now(),
      data,
    };

    this.push(entry);
    this.toConsole(entry);
    void this.toTauri(entry);
    this.listeners.forEach((l) => l(entry));
  }

  private push(entry: LogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length > MAX_BUFFER) {
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    }
  }

  private toConsole(entry: LogEntry): void {
    const label = `%c${entry.scope}`;
    const style = "color:#8a8a8a;font-weight:600";
    const args: unknown[] =
      entry.data === undefined
        ? [label, style, entry.message]
        : [label, style, entry.message, entry.data];

    switch (entry.level) {
      case "debug":
        console.debug(...args);
        break;
      case "info":
        console.info(...args);
        break;
      case "warn":
        console.warn(...args);
        break;
      case "error":
        console.error(...args);
        break;
    }
  }

  private async toTauri(entry: LogEntry): Promise<void> {
    if (!isTauri()) return;

    if (!this.tauriLog && !this.tauriLoadAttempted) {
      this.tauriLoadAttempted = true;
      try {
        const mod = await import("@tauri-apps/plugin-log");
        this.tauriLog = {
          debug: mod.debug,
          info: mod.info,
          warn: mod.warn,
          error: mod.error,
        };
      } catch {
        // Plugin unavailable — console mirroring is sufficient.
        this.tauriLog = null;
      }
    }

    const line = `[${entry.scope}] ${entry.message}`;
    try {
      await this.tauriLog?.[entry.level](line);
    } catch {
      // Never let logging throw into the caller.
    }
  }
}

/** The shared application logger. */
export const logger = new Logger();
